<template>
  <div class="page-table scrollable">
    <el-table
      :data="collection"
      :height="tableHeight"
      :row-style="{ 'max-height': '80px', height: '80px' }"
      @current-change="handleCurrentChange"
      @sort-change="sortChange"
      @row-click="expandRow"
      highlight-current-row
      stripe
      style="width: 100%"
      ref="cTable"
    >
      <div v-for="col in columns" :key="col.name">
        <el-table-column
          :key="col.name"
          v-if="col.datatype === 'expand'"
          type="expand"
          class-name="subtitle2"
          :label="col.label ? col.label : col.name"
          :min-width="col['min-width']"
          :width="col['min-width']"
        >
          <template slot-scope="props">
            <span v-if="col.component === 'ExpandAllowlistDetails'">
              <expand-allowlist-details
                :details="props.row"
                @delete-allowlist="handleDelete"
              ></expand-allowlist-details>
            </span>
            <span v-else-if="col.component === 'ExpandVmDetails'">
              <expand-vm-details :details="props.row"></expand-vm-details>
            </span>
            <span v-else-if="col.component === 'ExpandContainerDetails'">
              <expand-container-details :details="props.row"></expand-container-details>
            </span>
            <span v-else-if="col.component === 'ExpandAnomalyDetails'">
              <expand-anomaly-details :details="props.row"></expand-anomaly-details>
            </span>
            <span v-else-if="col.component === 'ExpandContainerImageDetails'">
              <expand-container-image-details :details="props.row"></expand-container-image-details>
            </span>
          </template>
        </el-table-column>

        <el-table-column
          v-else
          :type="col.datatype"
          :prop="col.name"
          :label="col.label ? col.label : col.name"
          :width="col.width"
          :min-width="col['min-width']"
          :row-key="currentRow"
          :sortable="col.sortable ? 'custom' : false"
          class-name="subtitle2"
        >
          <template slot-scope="scope">
            <div v-if="col.datatype === 'boolean'">
              <el-tag size="small">{{ scope.row[col.name] ? 'Active' : 'Inactive' }}</el-tag>
            </div>

            <div class="datetime-text" v-else-if="col.datatype === 'datetime'">
              <time>{{ getFormatedDateTime(scope.row, col) }}</time>
            </div>

            <div v-else-if="col.datatype === 'objectpath' && !col.display">
              <span class="format-text">{{ getObjectPathValue(scope.row, col.name) }}</span>
            </div>

            <div v-else-if="col.display === 'icon'">
              <span class="body1"> <i :class="getIcon(scope.row, col, col.displayKey).icon"></i> </span>
            </div>

            <div v-else-if="col.display === 'iconAndText'">
              <span class="body1">
                <i
                  class="display-inline-block"
                  :style="[
                    { color: getColorTagValue(scope.row, col, col.displayKey).color },
                    { background: getColorTagValue(scope.row, col, col.displayKey).color }
                  ]"
                  :class="getIcon(scope.row, col, col.displayKey).icon"
                ></i>
                {{ getColorTagValue(scope.row, col, col.displayKey).display }}
              </span>
            </div>

            <div v-else-if="col.display === 'colorTag'">
              <el-tag
                class="button2 capitalize-first-letter"
                size="small"
                :style="[
                  { color: getColorTagValue(scope.row, col, col.displayKey).textColor },
                  { 'border-color': getColorTagValue(scope.row, col, col.displayKey).textColor }
                ]"
                :color="getColorTagValue(scope.row, col, col.displayKey).color"
                >{{ getColorTagValue(scope.row, col, col.displayKey).display }}</el-tag
              >
            </div>

            <div v-else-if="col.display === 'stateTag'">
              <state-tag :status="scope.row[col.name]" :class-list="'button2 capitalize-first-letter'"/>
            </div>

            <div v-else-if="col.display === 'link'">
              <router-link v-if="col.query" :to="{ name: col.pathTo, query: getQueryParamObject(scope.row, col) }">
                <a v-if="col.linkLabel">{{ getObjectPathValue(scope.row, col.linkLabel.labelName) }}</a>
                <a
                  v-else-if="
                    $_.has(scope.row, 'protected') &&
                      scope.row.protected === 'No' &&
                      ['numProgressions', 'numDetections'].includes(col.name)
                  "
                >
                  {{ col.datatype === 'objectpath' ? getObjectPathValue(scope.row, col.name) : '?' }}
                </a>
                <a v-else>
                  <div>
                    {{ col.datatype === 'objectpath' ? getObjectPathValue(scope.row, col.name) : scope.row[col.name] }}
                    <img src="/static/images/Progression.svg" alt="icon" v-if="col.name === 'numProgressions'" />
                  </div>
                </a>
              </router-link>
              <router-link v-else :to="{ name: col.pathTo, params: getQueryParamObject(scope.row, col, 'params') }">
                <a v-if="col.linkLabel">{{ getObjectPathValue(scope.row, col.linkLabel.labelName) }}</a>
                <a v-else>
                  {{ col.datatype === 'objectpath' ? getObjectPathValue(scope.row, col.name) : scope.row[col.name] }}
                </a>
              </router-link>
            </div>

            <div v-else-if="col.display === 'mitrelink'">
              <span class="value">
                <a :href="getObjectPathValue(scope.row, col.link)" target="_blank" rel="noreferrer"
                  >{{ getObjectPathValue(scope.row, col.linkLabel.labelId) }}:
                  {{ getObjectPathValue(scope.row, col.linkLabel.labelName) }}</a
                >
              </span>
            </div>

            <div v-else-if="col.display === 'popup'">
              <el-link @click="emitCallbackEvent('popup-click', scope.row, col.name)">{{
                col.datatype === 'objectpath' ? getObjectPathValue(scope.row, col.name) : scope.row[col.name]
              }}</el-link>
            </div>

            <div v-else-if="col.display === 'riskscore'">
              <span class="body1">
                <i class="color-block" :class="`${scope.row.riskLevel.level}-risk-color`"></i>
                <span class="body1">{{ scope.row[col.name] }}</span>
              </span>
            </div>

            <div v-else-if="col.display === 'switch'">
              <span class="body1">
                <el-switch
                  v-model="scope.row[col.name]"
                  :width="40"
                  :active-value="col.displayKey.active.key"
                  :inactive-value="col.displayKey.inactive.key"
                  :active-text="
                    getObjectPathValue(scope.row, col.name) === col.displayKey.active.key
                      ? col.displayKey.active.display
                      : col.displayKey.inactive.display
                  "
                  :disabled="true"
                >
                </el-switch>
              </span>
            </div>

            <div v-else-if="col.display === 'toggle'">
              <span class="body1">
                <el-switch
                  v-model="scope.row[col.name]"
                  :width="40"
                  :active-text="scope.row[col.name] ? 'Active' : 'Paused'"
                >
                </el-switch>
              </span>
            </div>

            <div v-else-if="col.display === 'component'">
              <span v-if="col.component === 'CyberKillChainTickChart'">
                <cyber-kill-chain-tick-chart
                  :chart-id="`${scope.$index}`"
                  :chart-data="getObjectPathValue(scope.row, col.name)"
                  :key="`${scope.$index}_${getObjectPathValue(scope.row, col.componentKey)}`"
                />
                <a :href="getObjectPathValue(scope.row, col.link)" target="_blank" rel="noreferrer" v-if="col.link">
                  <span v-if="col.displayTextName">{{ getObjectPathValue(scope.row, col.displayTextName) }}</span>
                </a>
                <span v-else-if="col.displayTextName">{{ getObjectPathValue(scope.row, col.displayTextName) }}</span>
              </span>
              <span v-if="col.component === 'RecommendationProgress'">
                <recommendation-progress :row-data="scope.row" />
              </span>
              <span v-if="col.component === 'PercentComplete'">
                <percent-complete
                  :completed="getObjectPathValue(scope.row, col.componentFields.completed)"
                  :total="getObjectPathValue(scope.row, col.componentFields.total)"
                />
              </span>
              <span v-if="col.component === 'UserButton'">
                <user-button
                  :classList="getObjectPathValue(scope.row, col.name).firstname ? 'shamrock-200' : ''"
                  :user="getObjectPathValue(scope.row, col.name)"
                  :key="`${scope.$index}_${getObjectPathValue(scope.row, col.componentKey)}`"
                />
                {{ getObjectPathValue(scope.row, col.name).fullname }}
              </span>
              <span v-if="col.component === 'IntegrationStatusTableCell'">
                <integration-status-table-cell
                  :status="getObjectPathValue(scope.row, col.componentFields.status)"
                  :assetCount="getObjectPathValue(scope.row, col.componentFields.assetCount)"
                  :assetText="getObjectPathValue(scope.row, col.componentFields.assetText)"
                  :lastScanTime="getObjectPathValue(scope.row, col.componentFields.lastScanTime)"
                  :assetLink="col.componentFields.assetLink"
                  :accountType="getObjectPathValue(scope.row, col.componentFields.accountType)"
                  :accountKey="getObjectPathValue(scope.row, col.componentFields.accountKey)"
                  :nickName="getObjectPathValue(scope.row, col.componentFields.nickName)"
                />
              </span>
              <span v-if="col.component === 'AssetCountTableCell'">
                <asset-count-table-cell
                  :vmCount="getObjectPathValue(scope.row, col.componentFields.vmCount)"
                  :containerCount="getObjectPathValue(scope.row, col.componentFields.containerCount)"
                  :accountKey="getObjectPathValue(scope.row, col.componentFields.accountKey)"
                  :nickName="getObjectPathValue(scope.row, col.componentFields.nickName)"
                />
              </span>
              <span v-if="col.component === 'SensorCoverageTableCell'">
                <sensor-coverage-table-cell
                  :completed="getObjectPathValue(scope.row, col.componentFields.completed)"
                  :total="getObjectPathValue(scope.row, col.componentFields.total)"
                />
              </span>
              <span v-if="col.component === 'DiscoverySourceTableCell'">
                <discovery-source-table-cell
                  :icon="getIcon(scope.row, col, col.displayKey).icon"
                  :discoveryKey="getObjectPathValue(scope.row, col.componentFields.discoveryKey)"
                />
              </span>
              <span v-else-if="col.component === 'InstanceEntityTableCell'">
                <instance-entity-table-cell
                  :entities="getObjectPathValue(scope.row, col.componentFields.entities)"
                ></instance-entity-table-cell>
              </span>
              <span v-else-if="col.component === 'AnomalyDismissTableCell'">
                <anomaly-dismiss-table-cell
                  :entities="getObjectPathValue(scope.row, col.componentFields.entities)"
                  :state="getObjectPathValue(scope.row, col.componentFields.state)"
                  :uid="getObjectPathValue(scope.row, col.componentFields.uid)"
                ></anomaly-dismiss-table-cell>
              </span>
              <span v-if="col.component === 'NeedsAttentionTableCell'">
                <needs-attention-table-cell
                  :needsAttention="getObjectPathValue(scope.row, col.componentFields.needsAttention)"
                  :protected="getObjectPathValue(scope.row, 'protected')"
                  :managed="getObjectPathValue(scope.row, col.componentFields.managed)"
                  :upgradeable="getObjectPathValue(scope.row, col.componentFields.upgradeable)"
                  :numProgressions="getObjectPathValue(scope.row, col.componentFields.numProgressions)"
                  :status="getObjectPathValue(scope.row, col.componentFields.status)"
                  :accountKey="getObjectPathValue(scope.row, col.componentFields.accountKey)"
                  :nickName="getObjectPathValue(scope.row, col.componentFields.nickName)"
                />
              </span>
              <span v-if="col.component === 'ContainerImageNeedsAttentionTableCell'">
                <container-image-needs-attention-table-cell
                  :needsAttention="getObjectPathValue(scope.row, col.componentFields.needsAttention)"
                  :snykPresence="getObjectPathValue(scope.row, col.componentFields.snykPresence)"
                  :vulnLowScore="getObjectPathValue(scope.row, col.componentFields.vulnLowScore)"
                  :vulnMedScore="getObjectPathValue(scope.row, col.componentFields.vulnMedScore)"
                  :vulnHighScore="getObjectPathValue(scope.row, col.componentFields.vulnHighScore)"
                  :vulnerable="getObjectPathValue(scope.row, col.componentFields.vulnerable)"
                  :numProgressions="getObjectPathValue(scope.row, col.componentFields.numProgressions)"
                  :accountKey="getObjectPathValue(scope.row, col.componentFields.accountKey)"
                  :nickName="getObjectPathValue(scope.row, col.componentFields.nickName)"
                />
              </span>
              <span v-else-if="col.component === 'SnykVulnerableTableCell'">
                <snyk-vulnerable-table-cell
                  :snykPresence="getObjectPathValue(scope.row, col.componentFields.snykPresence)"
                  :vulnLowScore="getObjectPathValue(scope.row, col.componentFields.vulnLowScore)"
                  :vulnMedScore="getObjectPathValue(scope.row, col.componentFields.vulnMedScore)"
                  :vulnHighScore="getObjectPathValue(scope.row, col.componentFields.vulnHighScore)"
                  :vulnerable="getObjectPathValue(scope.row, col.componentFields.vulnerable)"
                ></snyk-vulnerable-table-cell>
              </span>
            </div>

            <div v-else-if="col.display === 'tag'">
              <el-tag size="medium" v-for="(item, idx) in scope.row[col.name]" :key="idx">{{ item }}</el-tag>
            </div>

            <span v-else-if="col.display === 'fingerprintLabel'" class="body1">
              {{ scope.row[col.display] }}
              <span
                v-if="scope.row[col.name]"
                class="display-inline-block icon-copy-to-clipboard cursor-pointer"
                @click="copyToClipboard(scope.row, col.name)"
              >
              </span>
            </span>

            <div v-else-if="col.display === 'multicolumn'">
              <span v-for="(eaCol, idx) in col.columns" :key="idx" class="display-block">
                <span class="display-inline-block"> {{ eaCol.label }}: </span>
                <span v-if="col.datatype === 'datetime'">
                  <time class="display-inline-block body1">{{ getFormatedDateTime(scope.row, col) }}</time>
                </span>
                <span v-else class="display-inline-block"> {{ getObjectPathValue(scope.row, eaCol.name) }} </span>
              </span>
            </div>

            <div v-else-if="col.datatype === 'array' || col.display === 'array'">
              <span v-for="(item, idx) in getObjectPathValue(scope.row, col.name)" :key="idx">
                {{ item }}
                <el-divider
                  v-if="idx !== getObjectPathValue(scope.row, col.name).length - 1"
                  direction="vertical"
                ></el-divider>
              </span>
            </div>

            <div class="format-text" v-else>
              <span>{{ scope.row[col.name] || 'N/A' }}</span>
            </div>
          </template>
        </el-table-column>
      </div>
    </el-table>
    <el-pagination
      background
      layout="prev, pager, next"
      :page-size="this.tableData.recordsPerPage"
      :total="this.tableData.totalRecords"
      :current-page="this.tableData.currentPage"
      prev-text="PREV"
      next-text="NEXT"
      hide-on-single-page
      style="text-align: center; padding-top: 20px;"
      @current-change="changePage"
    ></el-pagination>
  </div>
</template>

<script>
import BasicTableElement from '@/components/table/BasicTableElement.vue'

import RecommendationProgress from '@/components/chart/RecommendationProgress.vue'
import CyberKillChainTickChart from '@/components/chart/CyberKillChainTickChart.vue'
import UserButton from '@/components/button/UserButton.vue'
import {
  ExpandAllowlistDetails,
  ExpandVmDetails,
  ExpandContainerDetails,
  ExpandAnomalyDetails,
  ExpandContainerImageDetails
} from '@/components/expand'
import StateTag from "../button/StateTag.vue";

export default {
  components: {
    StateTag,
    UserButton,
    CyberKillChainTickChart,
    RecommendationProgress,
    ExpandAllowlistDetails,
    ExpandVmDetails,
    ExpandContainerDetails,
    ExpandAnomalyDetails,
    ExpandContainerImageDetails
  },
  name: 'ExpandableTableElement',
  extends: BasicTableElement,
  props: {
    heightOffset: { default: 0, type: Number },
    columns: { type: Array },
    tableData: { type: Object }
  },
  data () {
    return {
      currentRow: undefined,
      collection: [],
      tableHeight: 'auto'
    }
  },
  methods: {
    hasExpandCol () {
      return _.findIndex(this.columns, { datatype: 'expand' }) > -1
    },
    getHeader (h, { column, $index }) {
      return ''
    },
    isEmptyOrNull (row, colName) {
      return (
        (!_.isNumber(this.getObjectPathValue(row, colName)) &&
          (_.isEmpty(this.getObjectPathValue(row, colName)) ||
            this.getObjectPathValue(row, colName).toLowerCase() === 'null')) ||
        (_.isNumber(this.getObjectPathValue(row, colName)) && this.getObjectPathValue(row, colName) < 0)
      )
    },
    getBinaryHashLabel (row, hashField) {
      if (row[hashField] && !_.isNull(row[hashField]) && row[hashField].toLowerCase() !== 'null') {
        return `(${row[hashField].substring(0, 12)})`
      }
      return ''
    },
    highlightColumn (event) {
      if (this.hasExpandCol() && event.fromElement) {
        let rowExpandedEl = event.currentTarget
        rowExpandedEl = rowExpandedEl.closest('tr')
        rowExpandedEl.classList.add('hover')
        let mainRowEl = rowExpandedEl.previousElementSibling
        if (mainRowEl) {
          mainRowEl.classList.add('hover')
        }
      }
    },
    unhighlightColumn (event) {
      if (this.hasExpandCol() && event.fromElement) {
        let rowExpandedEl = event.currentTarget
        rowExpandedEl = rowExpandedEl.closest('tr')
        rowExpandedEl.classList.remove('hover')
        let mainRowEl = rowExpandedEl.previousElementSibling
        if (mainRowEl) {
          mainRowEl.classList.remove('hover')
        }
      }
    },
    handleDelete (id) {
      this.$emit('delete-allowlist', id)
    },
    expandRow (row, column, event) {
      if (this.$refs.cTable) {
        this.$refs.cTable.toggleRowExpansion(row)
      }
    }
  },
  mounted () {
    this.tableHeight = `${this.$el.parentElement.clientHeight - this.heightOffset - 60}`
    this.$nextTick(_ => {
      if (!this.tableData.models.length) {
        const el = document.getElementsByClassName('el-table__body-wrapper')
        el[0].style.display = 'none'
        el[0].style.height = '0px'
      }
    })
  }
}
</script>

<style scoped>
@import 'assets/styles/_variables.css';
@import 'assets/styles/colors.css';
@import 'assets/styles/tables.css';

>>> .el-table .cell {
  line-height: 16px;
  word-break: break-word;
}

>>> .el-table__expanded-cell {
  padding: 0px;
  > div {
    padding: 0px;

    .expanded-content {
      padding: 0px 10px;
      padding-bottom: 10px;
    }
  }
}
>>> .el-table__row {
  td {
    padding: 10px 0px 10px 0px;
    border-bottom: none !important;

    .cell {
      div.display-block {
        padding: 2px 0px;
      }
    }
  }
}

>>> .el-table__row--striped.expanded + tr {
  td {
    background: #fafafa;
  }
}

>>> .current-row.expanded + tr {
  td {
    background-color: #ecf5ff;
  }
}

>>> .el-table--enable-row-hover .el-table__body .el-table__row.expanded:hover + tr {
  td {
    background-color: #f5f7fa;
  }
}

>>> .el-table tr.hover {
  background-color: #f5f7fa;
}

>>> .el-table__row--striped.expanded.hover {
  td {
    background-color: #f5f7fa;
  }
}

>>> .el-table tr.current-row.hover {
  background-color: #f5f7fa;
  td {
    background-color: #f5f7fa;
  }
}

>>> .el-table .el-table__row--striped.current-row:hover {
  background-color: #f5f7fa;
  td {
    background-color: #f5f7fa;
  }
}

>>> .el-switch__label * {
  font-size: 13px;
}

>>> .el-switch__label--right {
  margin-left: 14.74px;
  height: 18px;
  color: #606266;
  width: 49px;
  letter-spacing: 0;
  line-height: 16px;
}

.subtitle2 {
  line-height: 16px;
}

.title {
  /* width: 115px; */
  background-color: var(--aluminum-300);
}

.title,
.edgeclass {
  font-weight: 600;
  padding: 0px 5px;
  border-radius: 2px;
}

.event_exec {
  background-color: var(--amaranth-50);
}

.event_file {
  background-color: var(--denim-50);
}

.event_network {
  background-color: var(--shamrock-50);
}

.event_system {
  background-color: var(--candlelight-100);
}

.event_user {
  background-color: var(--tuna-300);
}

.format-text {
  font-size: 13px;
  letter-spacing: 0;
  line-height: 16px;
  width: inherit;
}

.datetime-text {
  height: 16px;
  /* width: 50px; */
  font-size: 13px;
  letter-spacing: -0.11px;
  line-height: 16px;
}
</style>



// WEBPACK FOOTER //
// src/components/table/ExpandableTableElement.vue